import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Input } from '@bruno/ui';
import type { PublicCaptchaConfigDTO } from '@bruno/shared';
import { useAuth } from '../auth/AuthContext';
import { getCaptchaConfig, resendVerification, verifyEmail } from '../api/auth';
import { useCapCaptcha } from '../auth/useCapCaptcha';
import { CaptchaStatusBadge } from '../components/CaptchaStatusBadge';

type Status = 'verifying' | 'success' | 'invalid' | 'missing';

/**
 * Landing page for the link in the confirmation email
 * (`/verify-email?token=...`). It exchanges the token for a session
 * (auto sign-in) and forwards to the dashboard. If the link is invalid or
 * expired it offers a resend so the user is never stuck.
 */
export function VerifyEmailPage() {
  const { setSignedInUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'missing');
  // Guard against React StrictMode running the effect twice (which would
  // consume the single-use token twice and surface a spurious error).
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;
    void (async () => {
      try {
        const res = await verifyEmail(token);
        setSignedInUser(res.user);
        setStatus('success');
        setTimeout(() => navigate('/websites', { replace: true }), 900);
      } catch {
        // Expired, already-used, or otherwise unusable token. Surface the
        // resend path rather than a dead end.
        setStatus('invalid');
      }
    })();
  }, [token, navigate, setSignedInUser]);

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <Card className="w-full max-w-md text-center">
        {status === 'verifying' ? (
          <>
            <Spinner />
            <h1 className="mt-4 text-xl font-semibold">Confirming your email…</h1>
            <p className="mt-2 text-sm text-slate-500">This only takes a moment.</p>
          </>
        ) : null}

        {status === 'success' ? (
          <>
            <SuccessMark />
            <h1 className="mt-4 text-xl font-semibold">Email confirmed</h1>
            <p className="mt-2 text-sm text-slate-600">
              You&apos;re all set. Taking you to your dashboard…
            </p>
          </>
        ) : null}

        {status === 'missing' ? (
          <>
            <h1 className="text-xl font-semibold">Confirmation link incomplete</h1>
            <p className="mt-2 text-sm text-red-600">
              This link is missing its confirmation token. Use the most recent email we
              sent, or request a new one below.
            </p>
            <ResendBlock />
          </>
        ) : null}

        {status === 'invalid' ? (
          <>
            <h1 className="text-xl font-semibold">This link can&apos;t be used</h1>
            <p className="mt-2 text-sm text-slate-600">
              It may have expired or already been used. Enter your email and we&apos;ll send
              a fresh confirmation link.
            </p>
            <ResendBlock />
          </>
        ) : null}

        <div className="mt-6 border-t border-slate-100 pt-4 text-sm text-slate-500">
          <Link to="/login" className="text-indigo-600 hover:underline">
            Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}

/** Email input + resend button, with its own captcha handling. */
function ResendBlock() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [captchaCfg, setCaptchaCfg] = useState<PublicCaptchaConfigDTO | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getCaptchaConfig()
      .then((cfg) => {
        if (!cancelled) setCaptchaCfg(cfg);
      })
      .catch(() => {
        if (!cancelled) setCaptchaCfg({ enabled: false, instanceUrl: '', siteKey: '' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const captcha = useCapCaptcha(captchaCfg ?? undefined);

  async function onResend(e: FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(false);
    try {
      const token = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !token) throw new Error('captcha unavailable');
      await resendVerification(email, token ?? undefined);
      setSent(true);
    } catch {
      // Spent/failed captcha or a network error — drop the token, pre-solve
      // a fresh one, and let the user retry rather than claim a false send.
      if (captchaCfg?.enabled) captcha.reset();
      setError(true);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="mt-5 text-sm text-emerald-600">
        If that address needs confirming, a new link is on its way. Check your inbox
        (and spam).
      </div>
    );
  }

  return (
    <form onSubmit={onResend} className="mt-5 flex flex-col gap-3 text-left">
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoFocus
      />
      {error ? (
        <div className="text-sm text-red-600">Could not send just now. Please try again.</div>
      ) : null}
      <Button type="submit" loading={sending}>
        Send a new confirmation link
      </Button>
      {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
    </form>
  );
}

function Spinner() {
  return (
    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600" />
  );
}

function SuccessMark() {
  return (
    <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-50">
      <svg
        className="h-6 w-6 text-emerald-600"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </div>
  );
}
